'use client'

import { motion } from 'framer-motion'
import * as React from 'react'
import { Button } from '#app/components/ui/button'
import { Checkbox } from '#app/components/ui/checkbox'
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table'
import { cn } from '#app/utils/misc.tsx'

export interface GrantSubscription {
	id: string
	name: string
	territories: string
	fundingAreas: string
	fundingTypes: string
	isActive?: boolean
}

export interface GrantSubscriptionTableProps {
	subscriptions?: GrantSubscription[]
	onActivate?: (subscriptionId: string) => void
	onDeactivate?: (subscriptionId: string) => void
	onDelete?: (subscriptionIds: string[]) => void
	isSubmitting?: boolean
	isExampleData?: boolean
	className?: string
}

const defaultSubscriptions: GrantSubscription[] = [
	{
		id: '1',
		name: 'User Subscription',
		territories: 'Any',
		fundingAreas: 'Health',
		fundingTypes: 'Any',
		isActive: true,
	},
	{
		id: '2',
		name: 'User Subscription',
		territories: 'Canada',
		fundingAreas: 'Any',
		fundingTypes: 'Any',
		isActive: false,
	},
	{
		id: '3',
		name: 'My Research Grants',
		territories: 'USA, UK',
		fundingAreas: 'Technology, Science',
		fundingTypes: 'Research Projects',
		isActive: true,
	},
]

export default function GrantSubscriptionTable({
	subscriptions = defaultSubscriptions,
	onActivate,
	onDeactivate,
	onDelete,
	isSubmitting = false,
	isExampleData = false,
	className,
}: GrantSubscriptionTableProps) {
	const [selectedSubscriptions, setSelectedSubscriptions] = React.useState<
		Set<string>
	>(new Set())

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedSubscriptions(new Set(subscriptions.map(sub => sub.id)))
		} else {
			setSelectedSubscriptions(new Set())
		}
	}

	const handleSelectSubscription = (
		subscriptionId: string,
		checked: boolean,
	) => {
		const newSelected = new Set(selectedSubscriptions)
		if (checked) {
			newSelected.add(subscriptionId)
		} else {
			newSelected.delete(subscriptionId)
		}
		setSelectedSubscriptions(newSelected)
	}

	const selectedSubscriptionsList = subscriptions.filter(sub =>
		selectedSubscriptions.has(sub.id),
	)
	const hasSelection = selectedSubscriptions.size > 0
	const isAllSelected =
		subscriptions.length > 0 &&
		selectedSubscriptions.size === subscriptions.length
	const isIndeterminate =
		selectedSubscriptions.size > 0 &&
		selectedSubscriptions.size < subscriptions.length

	const handleActivateSelected = () => {
		selectedSubscriptionsList.forEach(sub => {
			if (!sub.isActive && onActivate) {
				onActivate(sub.id)
			}
		})
		setSelectedSubscriptions(new Set())
	}

	const handleDeactivateSelected = () => {
		selectedSubscriptionsList.forEach(sub => {
			if (sub.isActive && onDeactivate) {
				onDeactivate(sub.id)
			}
		})
		setSelectedSubscriptions(new Set())
	}

	const handleDeleteSelected = () => {
		if (onDelete && selectedSubscriptions.size > 0) {
			onDelete(Array.from(selectedSubscriptions))
		}
		setSelectedSubscriptions(new Set())
	}

	return (
		<motion.section
			aria-label="Your Funding Profiles"
			className={cn('w-full py-8', className)}
			initial={{
				opacity: 0,
				y: 20,
			}}
			animate={{
				opacity: 1,
				y: 0,
			}}
			transition={{
				duration: 0.5,
				delay: 0.3,
			}}
		>
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<h2 className="text-foreground mb-8 text-center text-3xl font-semibold md:text-left">
					{isExampleData ? 'Example Funding Profiles' : 'Your Funding Profiles'}
				</h2>
				{isExampleData && (
					<div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
						<p className="text-sm font-medium text-blue-800">
							📋 These are example funding profiles to demonstrate the
							platform's capabilities.
							<span className="font-normal">
								{' '}
								Register to create your own personalized profiles.
							</span>
						</p>
					</div>
				)}

				{/* Toolbar */}
				{hasSelection && !isExampleData && (
					<div className="mb-4 flex items-center gap-2 rounded-md border bg-gray-50 p-3">
						<span className="text-sm text-gray-600">
							{selectedSubscriptions.size} subscription
							{selectedSubscriptions.size !== 1 ? 's' : ''} selected
						</span>
						<div className="flex gap-2">
							{selectedSubscriptionsList.some(sub => !sub.isActive) && (
								<Button
									size="sm"
									onClick={handleActivateSelected}
									disabled={isSubmitting}
								>
									Activate
								</Button>
							)}
							{selectedSubscriptionsList.some(sub => sub.isActive) && (
								<Button
									size="sm"
									variant="outline"
									onClick={handleDeactivateSelected}
									disabled={isSubmitting}
								>
									Deactivate
								</Button>
							)}
							<Button
								size="sm"
								variant="destructive"
								onClick={handleDeleteSelected}
								disabled={isSubmitting}
							>
								Delete
							</Button>
						</div>
					</div>
				)}

				<div className="bg-card border-border overflow-x-auto rounded-lg border shadow-md">
					<Table className="min-w-full">
						<TableCaption className="text-muted-foreground py-4 text-sm">
							A list of your current funding profiles.
						</TableCaption>
						<TableHeader>
							<TableRow className="hover:bg-muted/50">
								{!isExampleData && (
									<TableHead className="w-12 px-6 py-4">
										<Checkbox
											checked={isAllSelected}
											onCheckedChange={handleSelectAll}
											aria-label="Select all subscriptions"
											className={
												isIndeterminate
													? 'data-[state=indeterminate]:bg-primary'
													: ''
											}
											{...(isIndeterminate && {
												'data-state': 'indeterminate',
											})}
										/>
									</TableHead>
								)}
								<TableHead className="text-muted-foreground whitespace-nowrap px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
									Name
								</TableHead>
								<TableHead className="text-muted-foreground whitespace-nowrap px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
									Status
								</TableHead>
								<TableHead className="text-muted-foreground w-64 px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
									Territories
								</TableHead>
								<TableHead className="text-muted-foreground w-64 px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
									Funding Areas
								</TableHead>
								<TableHead className="text-muted-foreground w-64 px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
									Funding Types
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{subscriptions.map((subscription, index) => (
								<TableRow
									key={subscription.id}
									className={cn(
										'border-border hover:bg-muted/30 border-b transition-colors duration-150',
										index % 2 === 0 ? 'bg-white' : 'bg-gray-50',
									)}
									aria-label={`Funding Profile: ${subscription.name}`}
								>
									{!isExampleData && (
										<TableCell className="px-6 py-4">
											<Checkbox
												checked={selectedSubscriptions.has(subscription.id)}
												onCheckedChange={checked =>
													handleSelectSubscription(subscription.id, !!checked)
												}
												aria-label={`Select ${subscription.name}`}
											/>
										</TableCell>
									)}
									<TableCell className="text-foreground whitespace-nowrap px-6 py-4 text-sm font-medium">
										{subscription.name || 'Funding Profile'}
									</TableCell>
									<TableCell className="whitespace-nowrap px-6 py-4 text-sm">
										<span
											className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
												subscription.isActive
													? 'bg-green-100 text-green-800'
													: 'bg-red-100 text-red-800'
											}`}
										>
											{subscription.isActive ? 'Active' : 'Inactive'}
										</span>
									</TableCell>
									<TableCell className="text-muted-foreground w-64 px-6 py-4 text-sm">
										{subscription.territories}
									</TableCell>
									<TableCell className="text-muted-foreground w-64 px-6 py-4 text-sm">
										{subscription.fundingAreas}
									</TableCell>
									<TableCell className="text-muted-foreground w-64 px-6 py-4 text-sm">
										{subscription.fundingTypes}
									</TableCell>
								</TableRow>
							))}
							{subscriptions.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={isExampleData ? 5 : 6}
										className="text-muted-foreground px-6 py-10 text-center"
									>
										You have no active funding profiles.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</motion.section>
	)
}
